import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { aiFeatures } from "./featuresData"; // Import AI Data
import Footer from "../../components/Footer";

// --- ★ ENHANCED BEAUTIFUL DARK GLASS FEATURE CARD ★ ---
const FeatureCard = ({ emoji, title, description, stat }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="h-full group"
  >
    {/* Premium gradient border */}
    <div className="relative h-full p-[1px] rounded-3xl bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 group-hover:from-yellow-400 group-hover:via-orange-400 group-hover:to-yellow-500 transition-all duration-500 shadow-lg group-hover:shadow-yellow-500/20 group-hover:-translate-y-2">
      {/* Inner glass */}
      <div className="relative h-full bg-[#053b21] rounded-[23px] p-8 flex flex-col justify-between overflow-hidden">
        {/* subtle glow */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/10 rounded-full blur-3xl -mr-16 -mt-16 transition-opacity duration-500 group-hover:opacity-30" />

        <div>
          {/* icon */}
          <div className="w-14 h-14 mb-6 flex items-center justify-center rounded-2xl bg-gray-800 border border-gray-700 shadow-inner group-hover:scale-110 group-hover:border-yellow-500/50 transition-all duration-300">
            <span className="text-3xl grayscale group-hover:grayscale-0 transition-all duration-300">
              {emoji}
            </span>
          </div>

          {/* title */}
          <h3 className="text-xl font-bold text-white mb-4 tracking-tight group-hover:text-yellow-400 transition-colors duration-300">
            {title}
          </h3>

          {/* desc */}
          <p className="text-gray-400 text-sm leading-relaxed font-medium">
            {description}
          </p>
        </div>

        {/* stat */}
        {stat && (
          <div className="mt-8 pt-6 border-t border-gray-800 flex items-center space-x-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-500"></span>
            </span>

            <span className="text-sm font-bold text-yellow-400 tracking-wide group-hover:text-white transition-colors duration-300">
              {stat}
            </span>
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

// --- Icons ---
const MagnifyIcon = ({ className = "w-3 h-3" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="5.5" />
    <path d="M15.5 15.5L20 20" />
  </svg>
);

const CloseIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

// --- MAIN COMPONENT ---
export default function FeatureDetailPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { featureId } = useParams();
  const feature = aiFeatures.find(
    (f) => f.id.toLowerCase() === featureId?.toLowerCase(),
  );
  const [openImage, setOpenImage] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpenImage(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!feature) {
    return (
      <div className="container mx-auto max-w-2xl p-8 text-center pt-32">
        <h1 className="text-4xl font-bold mb-4">Solution Not Found</h1>
        <Link
          to="/ai-security"
          className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700"
        >
          &larr; Back to AI Security
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION - SOLID DEEP GREEN #032d28 */}
      <section className="bg-[#032d28] pt-32 pb-24 relative overflow-hidden">
        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {feature.detailHeading || feature.heading}
              </h2>
              <h3 className="text-xl md:text-2xl text-green-400/90 font-medium mb-8">
                {feature.detailSubheadline}
              </h3>

              <div className="space-y-6 text-green-50/80">
                {feature.detailIntro && Array.isArray(feature.detailIntro) ? (
                  feature.detailIntro.map((paragraph, index) => (
                    <p
                      key={index}
                      className="text-lg md:text-xl leading-relaxed"
                    >
                      {paragraph}
                    </p>
                  ))
                ) : (
                  <p className="text-lg md:text-xl leading-relaxed">
                    {feature.detailIntro}
                  </p>
                )}
              </div>

              {feature.detailBullets && (
                <ul className="list-none p-0 space-y-4 mt-8">
                  {feature.detailBullets.map((bullet, index) => (
                    <li
                      key={index}
                      className="flex items-center text-lg text-green-100"
                    >
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-4 flex-shrink-0" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center"
            >
              <button
                onClick={() => setOpenImage(feature.imageUrl)}
                className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 group"
              >
                <img
                  src={feature.imageUrl}
                  alt={feature.heading}
                  className="w-full h-auto max-w-md object-contain transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BODY CONTENT - WHITE BG */}
      <div className="container mx-auto max-w-6xl px-4 pt-20 pb-20">
        {/* CARDS GRID */}
        {feature.cards && (
          <div className="mb-32">
            <h2 className="text-4xl font-bold text-center text-green-900 mb-16">
              Key Highlights
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 justify-center">
              {feature.cards.map((card) => (
                <FeatureCard key={card.title} {...card} />
              ))}
            </div>
          </div>
        )}

        {/* EXTRA SECTIONS */}
        {feature.extraSections && (
          <div className="space-y-32 border-t border-gray-100 pt-20">
            {feature.extraSections.map((section, index) => (
              <div
                key={index}
                className={`flex flex-col md:flex-row items-center gap-12 md:gap-20 ${index % 2 !== 0 ? "md:flex-row-reverse" : ""}`}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="flex-1"
                >
                  <h3 className="text-3xl md:text-4xl font-bold text-green-900 mb-6">
                    {section.title}
                  </h3>
                  {Array.isArray(section.description) ? (
                    <ul className="space-y-4 p-0 m-0 list-none">
                      {section.description.map((desc, i) => (
                        <li
                          key={i}
                          className="flex items-start text-lg md:text-xl text-gray-600 leading-relaxed"
                        >
                          <span className="mr-3 text-green-500 mt-1.5 flex-shrink-0 text-xl">
                            •
                          </span>
                          <span>{desc}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                      {section.description}
                    </p>
                  )}
                </motion.div>

                <div className="flex-1">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white group">
                    <button
                      onClick={() => setOpenImage(section.imageUrl)}
                      className="w-full"
                    >
                      <img
                        src={section.imageUrl}
                        alt={section.title}
                        className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </button>
                    <div className="absolute right-0 bottom-0 p-3 bg-black/50 rounded-tl-lg text-white pointer-events-none">
                      <MagnifyIcon className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-2xl md:text-3xl font-medium text-green-900/80 leading-relaxed mt-24 text-center max-w-4xl mx-auto italic">
          {feature.detailConclusion}
        </div>
      </div>

      <Footer />

      {/* Lightbox Modal */}
      <AnimatePresence>
        {openImage && (
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenImage(null)}
          >
            <motion.div
              className="relative z-20 max-w-6xl w-full flex items-center justify-center"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setOpenImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-green-400 transition-colors"
              >
                <CloseIcon className="w-8 h-8" />
              </button>
              <img
                src={openImage}
                alt="Fullscreen View"
                className="max-w-full max-h-[85vh] object-contain rounded-xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
